/**
 * Calendar event layout — interval-graph lane assignment for overlapping
 * timed events. Pure logic with no React dependency; extracted from
 * EventListModule so the algorithm can be exercised in isolation by
 * `event-layout.test.ts` (#1473 review).
 *
 * Used by the day-view and the per-day columns of the week-view to lay out
 * overlapping events side-by-side in equal-width sub-columns.
 */

// Date-parsing helpers live in dateTimeUtils.ts (owned by PR3). Re-exported
// here so `event-layout.test.ts` and existing importers keep their path/identity
// (the bodies were byte-identical; this is a pure dedup).
export { parseEventDate } from './dateTimeUtils';
import { parseEventDate } from './dateTimeUtils';

export type LaidOutEvent<E> = { event: E; lane: number; columns: number };

/**
 * #1473: Lay out overlapping timed events into side-by-side columns.
 *
 * Classic interval-graph lane assignment:
 *   1. Sort events by start time, then by end time descending (longer-first
 *      tiebreak so the "containing" event keeps the leftmost lane).
 *   2. For each event, pick the lowest-indexed lane not currently occupied
 *      by an active event.
 *   3. Group events into "clusters" of mutually-overlapping intervals; the
 *      max # of simultaneous lanes inside a cluster is what every event in
 *      that cluster uses as its width denominator.
 *
 * Returns, for each input event index, the `{ lane, columns }` it occupies.
 * Width is `1 / columns`; left offset is `lane / columns`.
 *
 * Zero-duration events are coerced to a 1-minute window so they still take
 * a lane (otherwise a coincident pair would render on top of each other).
 */
export function layoutOverlappingEvents<E extends { startAt: string; endAt?: string }>(
  events: readonly E[],
): LaidOutEvent<E>[] {
  if (events.length === 0) return [];
  type Interval = { idx: number; start: number; end: number; lane: number; clusterId: number };
  const intervals: Interval[] = events.map((e, idx) => {
    const start = (parseEventDate(e.startAt) ?? new Date(0)).getTime();
    const endRaw = e.endAt ? (parseEventDate(e.endAt) ?? new Date(start + 3600_000)) : new Date(start + 3600_000);
    const end = Math.max(endRaw.getTime(), start + 60_000); // min 1 minute so zero-length events still take a lane
    return { idx, start, end, lane: -1, clusterId: -1 };
  });
  // Sort by start ascending, tiebreak by end descending (longer first)
  intervals.sort((a, b) => (a.start - b.start) || (b.end - a.end));

  // Assign lanes and clusters together. A cluster is a chain where each new
  // event overlaps at least one currently-active event.
  let nextClusterId = 0;
  // Track currently active intervals to detect cluster boundaries
  const active: Interval[] = [];
  // Track cluster max width per id
  const clusterColumns = new Map<number, number>();

  for (const ivl of intervals) {
    // Drop active intervals that have ended before this one starts (they no
    // longer overlap with anything new — they only "block" lanes for events
    // that overlap them).
    for (let i = active.length - 1; i >= 0; i--) {
      if (active[i].end <= ivl.start) active.splice(i, 1);
    }
    // Determine cluster: if anything is still active, we're in its cluster.
    if (active.length === 0) {
      ivl.clusterId = nextClusterId++;
    } else {
      // Inherit the first active interval's cluster.
      ivl.clusterId = active[0].clusterId;
      // Merge any other active intervals into the same cluster (defensive —
      // shouldn't happen with the start-sorted scan, but if a later event
      // bridges two prior clusters they collapse here).
      for (const a of active) {
        if (a.clusterId !== ivl.clusterId) a.clusterId = ivl.clusterId;
      }
    }
    // Pick the lowest free lane within the active set.
    const occupied = new Set(active.map(a => a.lane));
    let lane = 0;
    while (occupied.has(lane)) lane++;
    ivl.lane = lane;
    active.push(ivl);
    // Track the max # of lanes ever simultaneously used in this cluster.
    const curMax = clusterColumns.get(ivl.clusterId) ?? 0;
    clusterColumns.set(ivl.clusterId, Math.max(curMax, active.length));
  }

  // Build result in original input order.
  const result: LaidOutEvent<E>[] = new Array(events.length);
  for (const ivl of intervals) {
    result[ivl.idx] = {
      event: events[ivl.idx],
      lane: ivl.lane,
      columns: clusterColumns.get(ivl.clusterId) ?? 1,
    };
  }
  return result;
}
